import type { ApiConfig, IdParams } from '@api/types';
import { typedMethodPost, typedMethodGet } from '@api/utils';
import type {
  MachineStatisticsRequestDto,
  MachineStatisticsResponseDto,
  NcFileStatisticsTimesRequestDto,
  NcFileStatisticsTimesResponseDto,
  MachineStatisticsOeeTeepRequestDto,
  MachineStatisticsOeeTeepResponseDto,
  ProductionBatchStatisticsRequestDto,
  ProductionBatchStatisticsResponseDto,
  ProductionBatchDto,
  NcFileStatisticsRequest2Dto,
  NcFileStatisticsResponse2Dto,
  NcFileStatisticsPaidRequestDto,
  NcFileStatisticsPaidResponseDto,
  MachineStatisticsPaidRequestDto,
  MachineStatisticsPaidResponseDto,
  MachineStatsHourOeeTeepDto,
  MachineStatsDayOeeTeepDto,
  MachineStatsMonthOeeTeepDto,
  MachineStatsWeekOeeTeepDto,
  MachineStatsHourAllOeeTeepDto,
  MachineStatsDayAllOeeTeepDto,
  MachineStatsMonthAllOeeTeepDto,
  MachineStatsWeekAllOeeTeepDto,
  MachineEventCycleStatisticsRequestDto,
  MachineEventCycleStatisticsResponseDto,
} from '../../generated/models';
import type { StatsODataParams, StatsAllODataParams } from './types';

import type {
  MachineStatisticsResponse,
  NcFileStatisticsTimesResponse,
  MachineStatisticsOeeTeepResponse,
  ProductionBatchStatisticsResponse,
  ProductionBatch,
  NcFileStatisticsResponse,
  NcFileStatisticsPaidResponse,
  MachineStatisticsPaidResponse,
  MachineStatsHourOeeTeep,
  MachineStatsDayOeeTeep,
  MachineStatsMonthOeeTeep,
  MachineStatsWeekOeeTeep,
  MachineStatsHourAllOeeTeep,
  MachineStatsDayAllOeeTeep,
  MachineStatsMonthAllOeeTeep,
  MachineStatsWeekAllOeeTeep,
  MachineEventCycleStatisticsResponse,
} from '../../domain/types';

import {
  toMachineStatisticsResponse,
  toNcFileStatisticsTimesResponse,
  toMachineStatisticsOeeTeepResponse,
  toProductionBatchStatisticsResponse,
  toProductionBatches,
  toNcFileStatisticsResponse,
  toNcFileStatisticsPaidResponse,
  toMachineStatisticsPaidResponse,
  toMachineStatsHourOeeTeeps,
  toMachineStatsDayOeeTeeps,
  toMachineStatsMonthOeeTeeps,
  toMachineStatsWeekOeeTeeps,
  toMachineStatsHourAllOeeTeeps,
  toMachineStatsDayAllOeeTeeps,
  toMachineStatsMonthAllOeeTeeps,
  toMachineStatsWeekAllOeeTeeps,
  toMachineEventCycleStatisticsResponse,
} from '../../domain/mappers';

export default ({ baseURL, baseURLoData }: ApiConfig) => ({
  machineStatistics: typedMethodPost<
    MachineStatisticsRequestDto,
    MachineStatisticsResponseDto,
    MachineStatisticsResponse
  >(
    (data) =>
      fetch(`${baseURL}/v2/statistics/machineStatistics`, {
        method: 'POST',
        ...data,
      }),
    toMachineStatisticsResponse
  ),
  ncFileStatisticsTimes: typedMethodPost<NcFileStatisticsTimesRequestDto, NcFileStatisticsTimesResponseDto, NcFileStatisticsTimesResponse>(
    (data) =>
      fetch(`${baseURL}/v1/statistics/ncFileStatisticsTimes`, {
        method: 'POST',
        ...data,
      }),
    toNcFileStatisticsTimesResponse
  ),
  machineStatisticsOeeTeep: typedMethodPost<
    MachineStatisticsOeeTeepRequestDto,
    MachineStatisticsOeeTeepResponseDto,
    MachineStatisticsOeeTeepResponse
  >(
    (data) =>
      fetch(`${baseURL}/v2/statistics/machineStatisticsOeeTeep`, {
        method: 'POST',
        ...data,
      }),
    toMachineStatisticsOeeTeepResponse
  ),
  productionBatchStatistics: typedMethodPost<ProductionBatchStatisticsRequestDto, ProductionBatchStatisticsResponseDto, ProductionBatchStatisticsResponse>(
    (data) =>
      fetch(`${baseURL}/v2/statistics/productionBatchStatistics`, {
        method: 'POST',
        ...data,
      }),
    toProductionBatchStatisticsResponse
  ),
  productionBatchData: typedMethodGet<IdParams, ProductionBatchDto[], ProductionBatch[]>(
    (data) =>
      fetch(`${baseURLoData}/odata/productionBatch?$filter=productionBatchId eq ${data.params.id}`, {
        method: 'GET',
        headers: data.headers,
        signal: data.signal,
      }),
    toProductionBatches
  ),
  ncFileStatistics: typedMethodPost<NcFileStatisticsRequest2Dto, NcFileStatisticsResponse2Dto, NcFileStatisticsResponse>(
    (data) =>
      fetch(`${baseURL}/v1/statistics/ncFileStatistics`, {
        method: 'POST',
        ...data,
      }),
    toNcFileStatisticsResponse
  ),
  ncFileStatisticsPaid: typedMethodPost<NcFileStatisticsPaidRequestDto, NcFileStatisticsPaidResponseDto, NcFileStatisticsPaidResponse>(
    (data) =>
      fetch(`${baseURL}/v1/statistics/ncFileStatisticsPaid`, {
        method: 'POST',
        ...data,
      }),
    toNcFileStatisticsPaidResponse
  ),
  machineStatisticsPaid: typedMethodPost<MachineStatisticsPaidRequestDto, MachineStatisticsPaidResponseDto, MachineStatisticsPaidResponse>(
    (data) =>
      fetch(`${baseURL}/v2/statistics/machineStatisticsPaid`, {
        method: 'POST',
        ...data,
      }),
    toMachineStatisticsPaidResponse
  ),
  machineStatsHourOeeTeep: typedMethodGet<StatsODataParams, MachineStatsHourOeeTeepDto[], MachineStatsHourOeeTeep[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/machineStatsHourOeeTeep?$filter=machineId eq ${data.params.id} and dateTime ge ${data.params.start} and dateTime lt ${data.params.end}&$orderby=dateTime asc`,
        {
          method: 'GET',
          headers: data.headers,
          signal: data.signal,
        }
      ),
    toMachineStatsHourOeeTeeps
  ),
  machineStatsDayOeeTeep: typedMethodGet<StatsODataParams, MachineStatsDayOeeTeepDto[], MachineStatsDayOeeTeep[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/machineStatsDayOeeTeep?$filter=machineId eq ${data.params.id} and date ge ${data.params.start} and date lt ${data.params.end}&$orderby=date asc`,
        {
          method: 'GET',
          headers: data.headers,
          signal: data.signal,
        }
      ),
    toMachineStatsDayOeeTeeps
  ),
  machineStatsMonthOeeTeep: typedMethodGet<StatsODataParams, MachineStatsMonthOeeTeepDto[], MachineStatsMonthOeeTeep[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/machineStatsMonthOeeTeep?$filter=machineId eq ${data.params.id} and Date ge ${data.params.start} and Date lt ${data.params.end}&$orderby=Date asc`,
        {
          method: 'GET',
          headers: data.headers,
          signal: data.signal,
        }
      ),
    toMachineStatsMonthOeeTeeps
  ),
  machineStatsWeekOeeTeep: typedMethodGet<StatsODataParams, MachineStatsWeekOeeTeepDto[], MachineStatsWeekOeeTeep[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/machineStatsWeekOeeTeep?$filter=machineId eq ${data.params.id} and date ge ${data.params.start} and date lt ${data.params.end}&$orderby=date asc`,
        {
          method: 'GET',
          headers: data.headers,
          signal: data.signal,
        }
      ),
    toMachineStatsWeekOeeTeeps
  ),
  machineStatsHourAllOeeTeep: typedMethodGet<StatsAllODataParams, MachineStatsHourAllOeeTeepDto[], MachineStatsHourAllOeeTeep[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/machineStatsHourAllOeeTeep?$filter=dateTime ge ${data.params.start} and dateTime lt ${data.params.end}&$orderby=dateTime asc`,
        {
          method: 'GET',
          headers: data.headers,
          signal: data.signal,
        }
      ),
    toMachineStatsHourAllOeeTeeps
  ),
  machineStatsDayAllOeeTeep: typedMethodGet<StatsAllODataParams, MachineStatsDayAllOeeTeepDto[], MachineStatsDayAllOeeTeep[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/machineStatsDayAllOeeTeep?$filter=date ge ${data.params.start} and date lt ${data.params.end}&$orderby=date asc`,
        {
          method: 'GET',
          headers: data.headers,
          signal: data.signal,
        }
      ),
    toMachineStatsDayAllOeeTeeps
  ),
  machineStatsMonthAllOeeTeep: typedMethodGet<StatsAllODataParams, MachineStatsMonthAllOeeTeepDto[], MachineStatsMonthAllOeeTeep[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/machineStatsMonthAllOeeTeep?$filter=Date ge ${data.params.start} and Date lt ${data.params.end}&$orderby=Date asc`,
        {
          method: 'GET',
          headers: data.headers,
          signal: data.signal,
        }
      ),
    toMachineStatsMonthAllOeeTeeps
  ),
  machineStatsWeekAllOeeTeep: typedMethodGet<StatsAllODataParams, MachineStatsWeekAllOeeTeepDto[], MachineStatsWeekAllOeeTeep[]>(
    (data) =>
      fetch(
        `${baseURLoData}/odata/machineStatsWeekAllOeeTeep?$filter=date ge ${data.params.start} and date lt ${data.params.end}&$orderby=date asc`,
        {
          method: 'GET',
          headers: data.headers,
          signal: data.signal,
        }
      ),
    toMachineStatsWeekAllOeeTeeps
  ),
  machineEventCycleStatistics: typedMethodPost<MachineEventCycleStatisticsRequestDto, MachineEventCycleStatisticsResponseDto, MachineEventCycleStatisticsResponse>(
    (data) =>
      fetch(`${baseURL}/v2/statistics/machineEventCycleStatistics`, {
        method: 'POST',
        ...data,
      }),
    toMachineEventCycleStatisticsResponse
  ),
});

// Re-export domain types for consumers
export type {
  MachineStatisticsResponse,
  NcFileStatisticsTimesResponse,
  MachineStatisticsOeeTeepResponse,
  ProductionBatchStatisticsResponse,
  ProductionBatch,
  NcFileStatisticsResponse,
  NcFileStatisticsPaidResponse,
  MachineStatisticsPaidResponse,
  MachineStatsHourOeeTeep,
  MachineStatsDayOeeTeep,
  MachineStatsMonthOeeTeep,
  MachineStatsWeekOeeTeep,
  MachineStatsHourAllOeeTeep,
  MachineStatsDayAllOeeTeep,
  MachineStatsMonthAllOeeTeep,
  MachineStatsWeekAllOeeTeep,
  MachineEventCycleStatisticsResponse,
} from '../../domain/types';

// Re-export request DTOs (still needed for building requests)
export type {
  MachineStatisticsRequestDto,
  NcFileStatisticsTimesRequestDto,
  MachineStatisticsOeeTeepRequestDto,
  ProductionBatchStatisticsRequestDto,
  NcFileStatisticsRequest2Dto,
  NcFileStatisticsPaidRequestDto,
  MachineStatisticsPaidRequestDto,
  MachineEventCycleStatisticsRequestDto,
} from '../../generated/models';

export type { StatsODataParams, StatsAllODataParams } from './types';
